import { PaperAirplaneIcon, ShoppingCartIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import { ChangeEventHandler, memo, useCallback, useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Card } from '../../components/Card';
import { ALL_TIME, DateRange, DateRangePicker, inRange, UPCOMING_PRESETS } from '../../components/DateRangePicker';
import { FilterChips, FilterGroup } from '../../components/FilterChips';
import { Input } from '../../components/inputs/Input';
import { ListItem } from '../../components/ListItem';
import { EarthLoader } from '../../components/loaders/EarthLoader';
import { PageContent } from '../../components/page/PageContent';
import { PageHeader } from '../../components/page/PageHeader';
import { DotsPopover } from '../../components/Popover/DotsPopover';
import { StatsProperty, SubscriptionPill, useStatisticsTranslations } from '../../components/Stats';
import { DowngradeTag, SubscriptionStatusTags, subscriptionStatusesOf, useSubscriptionStatusLabels } from '../../components/SubscriptionStatusTags';
import { usePlanOptions } from '../../hooks/usePlanOptions';
import useOpen from '../../hooks/useOpen';
import { CreateCustomerOrganisationModal, CustomerOrganisationValues } from '../../modules/organisation/customers/CreateCustomerOrganisationModal';
import { InviteUsersModal } from '../../modules/organisation/customers/InviteUsersModal';
import { T, useT } from '../../translation/src';
import {
  OrganisationReferralOverviewDocument,
  OrganisationReferralOverviewQuery,
  ReferralOverviewFieldsFragment,
  ReferralRole,
  ReferralSaleFieldsFragment,
  SubscriptionStatusFilter,
  SubscriptionType,
  useOrganisationReferralCustomerCreateMutation,
  useOrganisationReferralOverviewQuery,
} from '../../types/graphqlTypes';
import { useFormatDate } from '../../hooks/useFormatDate';
import { CustomerSubscriptionModal } from '../../modules/organisation/customers/CustomerSubscriptionModal';
import { ordersForCustomers } from '../../modules/organisation/referrals/partnerRoles';
import { usePartnerTerms } from '../../modules/organisation/referrals/usePartnerTerms';
import { dayAfterTermEnd } from '../../utils/dates';
import { formatPrice } from '../../utils/formatPrice';

type Customer = ReferralOverviewFieldsFragment['referredOrganisations'][number];

// A type (not an interface) so it fits FilterValues' index signature.
type CustomerFilters = {
  subscriptionType?: SubscriptionType[];
  status?: SubscriptionStatusFilter[];
};
const NO_FILTERS: CustomerFilters = {};

// Cancelled: the term ends and is not renewed.
const isEnding = (customer: Customer) => customer.scheduledType === SubscriptionType.Flex;

const statusesOf = (customer: Customer) => subscriptionStatusesOf({ paused: customer.subscriptionPaused, scheduledType: customer.scheduledType });

// The day the card shows for the term: the day it renews, or, cancelled, the day it ends. None on Flex.
const renewalDayOf = (customer: Customer): Date | undefined => {
  if (!customer.subscriptionEndsAt || customer.subscriptionType === SubscriptionType.Flex) return undefined;
  return isEnding(customer) ? new Date(customer.subscriptionEndsAt) : dayAfterTermEnd(customer.subscriptionEndsAt);
};

// The admin center's plan filter, without Custom (a plan only Pythagoras sets up, never a partner's customer's), and
// the subscription's status.
const useFilterGroups = (): FilterGroup<CustomerFilters>[] => {
  const plans = usePlanOptions();
  const statuses = useSubscriptionStatusLabels();
  const subscription = useT('subscription', { swc: true });
  const status = useT('status', { swc: true });
  return useMemo(
    () => [
      { key: 'subscriptionType', title: subscription, options: plans.filter((plan) => plan.value !== SubscriptionType.Custom) },
      { key: 'status', title: status, options: Object.values(SubscriptionStatusFilter).map((value) => ({ value, label: statuses[value] })) },
    ],
    [plans, status, statuses, subscription],
  );
};

// Each filter keeps the customers matching any of its values; the filters, the renewal period and the search
// narrow each other. A renewal period leaves out the customers without one (Flex).
const matches = (customer: Customer, filters: CustomerFilters, search: string, renewal: DateRange): boolean => {
  const renewalDay = renewalDayOf(customer);
  return (
    (!filters.subscriptionType?.length || filters.subscriptionType.includes(customer.subscriptionType)) &&
    (!filters.status?.length || statusesOf(customer).some((status) => filters.status?.includes(status))) &&
    (renewal.preset === ALL_TIME.preset || (!!renewalDay && inRange(renewalDay, renewal))) &&
    (!search || customer.name.toLowerCase().includes(search))
  );
};

// A customer as the partner sees it: like the admin center's card, with what the partner may do — order for it
// when it is billed to the partner, invite people into it, visit it (a full-service partner only) — and what it
// brought in.
const CustomerCard_: React.FC<{ customer: Customer; organisationId: string; role: ReferralRole; sales: ReferralSaleFieldsFragment[]; cuts: { first: number; recurring: number } }> = ({ customer, organisationId, role, sales, cuts }) => {
  const translations = useStatisticsTranslations();
  const { formatDay } = useFormatDate();
  const terms = usePartnerTerms(role);
  const renewsTitle = useT('renews on', { swc: true });
  const endsTitle = useT('ends on', { swc: true });
  const { onClose: closeInviteModal, open: inviteModalOpen, onOpen: openInviteModal } = useOpen();
  const { onClose: closeSubscriptionModal, open: subscriptionModalOpen, onOpen: openSubscriptionModal } = useOpen();
  const ending = isEnding(customer);
  const renewalDay = renewalDayOf(customer);
  const onVisit = useCallback(() => {
    window.open(`/organisations/${customer.id}/projects`, '_blank');
  }, [customer.id]);

  return (
    <Card noPadding>
      <div className="relative flex items-center bg-gray dark:bg-gray-900">
        <span className="p-4">{customer.name}</span>
        <SubscriptionStatusTags statuses={statusesOf(customer)} />
        <DowngradeTag scheduledType={customer.scheduledType} />
        <DotsPopover>
          {({ open }) =>
            open ? (
              <>
                {customer.billedToYou && (
                  <ListItem onClick={openSubscriptionModal} icon={ShoppingCartIcon}>
                    <T _str="order" swc />
                  </ListItem>
                )}
                {customer.canVisit && (
                  <ListItem onClick={onVisit} icon={PaperAirplaneIcon}>
                    <T _str="Go to organisation" swc />
                  </ListItem>
                )}
                {(customer.canVisit || customer.billedToYou) && (
                  <ListItem onClick={openInviteModal} icon={UserPlusIcon}>
                    <T _str="invite people" swc />
                  </ListItem>
                )}
              </>
            ) : null
          }
        </DotsPopover>
      </div>
      <div className="flex flex-col px-4 py-2 divide-y divide-gray-700">
        <StatsProperty title={translations.projects} count={customer.projectsCount} />
        <StatsProperty title={translations.subscription}>
          <span className="flex justify-end">
            <SubscriptionPill subscription={{ type: customer.subscriptionType, endDate: customer.subscriptionEndsAt }} />
          </span>
        </StatsProperty>
        {renewalDay && <StatsProperty title={ending ? endsTitle : renewsTitle}>{formatDay(renewalDay)}</StatsProperty>}
        <StatsProperty title={translations.joinedDate} date={customer.joinedAt} />
        <StatsProperty title={translations.users} count={customer.usersCount} />
        <StatsProperty title={terms.deals}>
          {customer.salesCount} · {formatPrice(customer.salesAmountCents)}
          {customer.awaitingPaymentCount > 0 && (
            <span className="ml-1 text-xs text-gray-500">
              (<T _str="{count} awaiting payment" args={{ count: customer.awaitingPaymentCount }} />)
            </span>
          )}
        </StatsProperty>
        <StatsProperty title={terms.share}>{formatPrice(customer.cutCents)}</StatsProperty>
      </div>
      {/* A reseller does not look into its customers: its invitations go through it (as admins). */}
      <InviteUsersModal open={inviteModalOpen} onClose={closeInviteModal} organisationId={customer.id} organisationName={customer.name} partnerOrganisationId={customer.canVisit ? undefined : organisationId} />
      {customer.billedToYou && <CustomerSubscriptionModal open={subscriptionModalOpen} onClose={closeSubscriptionModal} organisationId={organisationId} customer={customer} sales={sales} cuts={cuts} role={role} />}
    </Card>
  );
};
const CustomerCard = memo(CustomerCard_);

// A reseller's or full-service partner's customers: the organisations it acquired, manages or is billed for, each
// with its orders and a way to bring people in, and a way to make a new one. The referrals page's numbers, in the
// admin center's shape.
const CustomerCenter_: React.FC2 = () => {
  const { organisationId = '' } = useParams();
  const searchPlaceholder = useT('search customer', { swc: true });
  const { data, loading } = useOrganisationReferralOverviewQuery({ variables: { organisationId }, fetchPolicy: 'cache-and-network' });
  // ReferralOverview has no id: the answer is written over the page's query by hand (see Referrals).
  const [create] = useOrganisationReferralCustomerCreateMutation({
    update: (cache, { data: created }) => {
      if (!created) return;
      cache.writeQuery<OrganisationReferralOverviewQuery>({ query: OrganisationReferralOverviewDocument, variables: { organisationId }, data: { organisationReferralOverview: created.organisationReferralCustomerCreate } });
    },
  });
  const { onClose: closeCreateModal, open: createModalOpen, onOpen: openCreateModal } = useOpen();
  const [search, setSearch] = useState('');
  const onChangeSearch: ChangeEventHandler<HTMLInputElement> = useCallback((event) => setSearch(event.target.value), []);
  const overview = data?.organisationReferralOverview;
  const cuts = useMemo(() => ({ first: overview?.firstCutBasisPoints ?? 0, recurring: overview?.recurringCutBasisPoints ?? 0 }), [overview?.firstCutBasisPoints, overview?.recurringCutBasisPoints]);
  const [filters, setFilters] = useState<CustomerFilters>(NO_FILTERS);
  const [renewal, setRenewal] = useState<DateRange>(ALL_TIME);
  const filterGroups = useFilterGroups();
  const filtered = !!search.trim() || renewal.preset !== ALL_TIME.preset || !!filters.subscriptionType?.length || !!filters.status?.length;
  const customers = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (overview?.referredOrganisations ?? []).filter((customer) => matches(customer, filters, term, renewal));
  }, [filters, overview?.referredOrganisations, renewal, search]);

  const onCreate = useCallback(
    async ({ name, emails }: CustomerOrganisationValues) => {
      await create({ variables: { organisationId, name, emails } });
    },
    [create, organisationId],
  );

  // Only a partner that orders for its customers has this page: anyone else is sent back to the referrals page.
  if (overview && !ordersForCustomers(overview.role)) return <Navigate to={`/organisations/${organisationId}/referrals`} replace />;

  return (
    <PageContent>
      <PageHeader title={<T _str="customers" swc />} buttonTitle={<T _str="customer" swc />} onClickButton={openCreateModal} />
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Input className="w-52" placeholder={`${searchPlaceholder}...`} value={search} onChange={onChangeSearch} />
        <span className="flex items-center gap-2 text-sm text-gray-500">
          <T _str="renewal date" swc />
          <DateRangePicker value={renewal} onChange={setRenewal} presets={UPCOMING_PRESETS} />
        </span>
        <FilterChips groups={filterGroups} value={filters} onChange={setFilters} defaults={NO_FILTERS} />
      </div>
      <div className="w-full min-h-0 flex-1 overflow-y-auto">
        {loading && !overview ? (
          <EarthLoader />
        ) : customers.length === 0 ? (
          <div className="py-10 text-sm text-center text-gray-500">{filtered ? <T _str="No customer matches your search." /> : <T _str="No customers yet. Add one, or share your link." />}</div>
        ) : (
          <div className="grid w-full gap-4 pb-10 rounded-md grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
            {customers.map((customer) => (
              <CustomerCard key={customer.id} customer={customer} organisationId={organisationId} role={overview?.role ?? ReferralRole.Reseller} sales={overview?.sales ?? []} cuts={cuts} />
            ))}
          </div>
        )}
      </div>
      <CreateCustomerOrganisationModal open={createModalOpen} onClose={closeCreateModal} title={<T _str="add customer" swc />} onCreate={onCreate} />
    </PageContent>
  );
};
export const CustomerCenter = memo(CustomerCenter_);
